import { FIELD_TYPE_CATALOGUE, type FieldType } from '@crm/contracts';
import { Input } from '../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { Textarea } from '../ui/input.tsx';

export interface EditorProps {
  type: FieldType;
  value: unknown;
  onChange: (value: unknown) => void;
  options?: ReadonlyArray<{ value: string; label: string }> | undefined;
  disabled?: boolean;
  id?: string;
  label: string;
}

/**
 * Edit a value by its field TYPE (RELAY-941): the catalogue's `editor` decides. Values are
 * kept in the API's wire shape (ISO strings, minor-unit currency, string arrays) so the form
 * can post them as they are.
 */
export function FieldEditor({ type, value, onChange, options, disabled, id, label }: EditorProps) {
  const editor = FIELD_TYPE_CATALOGUE[type].editor;
  const text =
    value === null || value === undefined
      ? ''
      : typeof value === 'string'
        ? value
        : typeof value === 'number' || typeof value === 'boolean'
          ? String(value)
          : JSON.stringify(value);
  switch (editor) {
    case 'textarea':
      return (
        <Textarea
          id={id}
          aria-label={label}
          disabled={disabled}
          value={text}
          onChange={(e) => onChange(e.target.value || null)}
          className="min-h-20"
        />
      );
    case 'number':
      return (
        <Input
          id={id}
          aria-label={label}
          type="number"
          step="any"
          disabled={disabled}
          value={text}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'currency': {
      const c = (value as { amountMinor?: number; currency?: string } | null) ?? null;
      return (
        <div className="flex gap-2">
          <Input
            id={id}
            aria-label={label}
            type="number"
            step="0.01"
            disabled={disabled}
            value={c && typeof c.amountMinor === 'number' ? (c.amountMinor / 100).toString() : ''}
            onChange={(e) =>
              onChange(
                e.target.value === ''
                  ? null
                  : {
                      amountMinor: Math.round(Number(e.target.value) * 100),
                      currency: c?.currency ?? 'USD',
                    },
              )
            }
          />
          <Input
            aria-label={`${label} currency`}
            className="w-20"
            maxLength={3}
            disabled={disabled}
            value={c?.currency ?? 'USD'}
            onChange={(e) =>
              onChange({ amountMinor: c?.amountMinor ?? 0, currency: e.target.value.toUpperCase() })
            }
          />
        </div>
      );
    }
    case 'checkbox':
      return (
        <input
          id={id}
          aria-label={label}
          type="checkbox"
          disabled={disabled}
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
          className="size-4"
        />
      );
    case 'date':
      return (
        <Input
          id={id}
          aria-label={label}
          type="date"
          disabled={disabled}
          value={text.slice(0, 10)}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    case 'datetime':
      return (
        <Input
          id={id}
          aria-label={label}
          type="datetime-local"
          disabled={disabled}
          value={text ? text.slice(0, 16) : ''}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
        />
      );
    case 'select':
      return (
        <Select
          {...(text ? { value: text } : {})}
          onValueChange={(v) => onChange(v)}
          disabled={disabled ?? false}
        >
          <SelectTrigger id={id} aria-label={label}>
            <SelectValue placeholder="Choose…" />
          </SelectTrigger>
          <SelectContent>
            {(options ?? []).map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'multiselect': {
      const chosen = new Set(Array.isArray(value) ? (value as string[]) : []);
      return (
        <div className="flex flex-wrap gap-3" role="group" aria-label={label}>
          {(options ?? []).map((o) => (
            <label key={o.value} className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                disabled={disabled}
                checked={chosen.has(o.value)}
                onChange={(e) => {
                  const next = new Set(chosen);
                  if (e.target.checked) next.add(o.value);
                  else next.delete(o.value);
                  onChange(next.size ? [...next] : null);
                }}
              />
              {o.label}
            </label>
          ))}
        </div>
      );
    }
    case 'json':
      return (
        <Textarea
          id={id}
          aria-label={label}
          disabled={disabled}
          defaultValue={value === null || value === undefined ? '' : JSON.stringify(value)}
          onBlur={(e) => {
            try {
              onChange(e.target.value.trim() ? (JSON.parse(e.target.value) as unknown) : null);
            } catch (err) {
              console.warn('not valid JSON; keeping the previous value', err);
              onChange(value);
            }
          }}
          className="min-h-20 font-mono text-xs"
        />
      );
    default:
      return (
        <Input
          id={id}
          aria-label={label}
          disabled={disabled}
          value={text}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
  }
}
